import { Badge, Button, Image, List, Select, Skeleton, Space, Typography } from "antd";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import styled, { createGlobalStyle } from "styled-components";
import { ReactComponent as BellFilled } from "../assets/svg/bell.svg";

import { CloseOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { APStatus } from "../constant/assistanceProgram";
import { formPopupsContentKeys } from "../constant/formPopupsContentKeys";
import { Routes } from "../constant/routes";
import ACTIONS from "../store/action";
import {
  selectFormPopups,
  selectUser,
  selectEnvironment,
  selectNotificationDisplayFilter,
  selectBadgeCounts
} from "../store/selector";
import { dateTimeFormatter } from "../utils/date";
import { trackPendoEvent } from "../utils/pendo/pendoUtils";
import { isUrl } from "../utils/formValidation";
import { capitalizeFirstLetter } from "../utils/string";
import { getNotificationsAPI, markNotificationAPI } from "./../api/api";
import { BLACK, BLUE, PRIMARY_BLUE, SEC_FONT_GREY, GRAY_500, WHITE } from "./../constant/colors";
import ErrorMessage from "./customComponent/customMessages/ErrorMessage";
import { Popover } from "./UI/Antd/Popover/Popover";
import { CircleIconButton } from "./UI/CircleIconButton";
import { Tooltip } from "./UI/Tooltip";
import { NotificationSettingsIcon } from "../assets/svg/NotificationSettingsIcon";
import { NoFundsFoundIcon } from "../assets/svg/NoFundsFoundIcon";
import { history } from "../router/history";
import { DisplayFilterMap } from "../utils/notificationFilters";

const { Option } = Select;
const { Text } = Typography;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    padding-left: 0 !important;
    width: 190px !important;
    border-bottom: 1px solid black !important;
    border-radius: 0 !important;
    font-size: 12px !important;
    color: ${PRIMARY_BLUE} !important;
  }

  .ant-select-selection-item {
    color: {PRIMARY_BLUE} !important;
  }

  .ant-select-selection-placeholder {
    color: ${PRIMARY_BLUE} !important;
  }
`;

const NotificationDropdownStyles = createGlobalStyle`
  .ant-select-dropdown.notification-dropdown {
    background-color: ${WHITE} !important;
    
    .ant-select-item {
      background-color: ${WHITE} !important;
      font-weight: 600 !important;
      font-size: 12px !important;
      transition: background 0.3s ease !important;
      
    }
    
    .ant-select-item-option-selected,
    .ant-select-item-option-active,
    .ant-select-item.ant-select-item-option-selected,
    .ant-select-item.ant-select-item-option-active {
      background: linear-gradient(90deg, #ffffff 0%, #eff8ff 100%) !important;
      color: ${PRIMARY_BLUE} !important;
      position: relative !important;
    }
    
    .ant-select-item-option-selected::after,
    .ant-select-item.ant-select-item-option-selected::after {
      content: '' !important;
      position: absolute !important;
      right: 5px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      width: 8px !important;
      height: 8px !important;
      background-color: ${PRIMARY_BLUE} !important;
      border-radius: 50% !important;
    }
  }
`;

const OrderSortMap = Object.freeze({
  newToOld: {
    key: "newToOld",
    text: (t) => t("notifications.order_sort.date_new_to_old"),
    sort: (newList) => newList
  },
  oldToNew: {
    key: "oldToNew",
    text: (t) => t("notifications.order_sort.date_old_to_new"),
    sort: (newList) => _.sortBy(newList, "createdAt")
  },
  status: {
    key: "status",
    text: (t) => t("notifications.order_sort.status_open_to_close"),
    sort: (newList) => {
      return _.sortBy(newList, (item) => item.assistanceProgram.status || "").reverse();
    }
  },
  unread: {
    key: "unread",
    text: (t) => t("notifications.order_sort.unread_first"),
    sort: (newList) => _.sortBy(newList, "read")
  }
});

const NotificationsComponent = ({ t }) => {
  const dispatch = useDispatch();

  const storedDisplayFilter = useSelector(selectNotificationDisplayFilter);
  const [displayFilter, setDisplayFilter] = useState(storedDisplayFilter || DisplayFilterMap.unread.key);
  const [orderFilter, setOrderFilter] = useState(OrderSortMap.newToOld.key);
  const [notifications, setNotifications] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [hoveredNotificationId, setHoveredNotificationId] = useState(null);

  const formPopups = useSelector(selectFormPopups);
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const badgeCounts = useSelector(selectBadgeCounts);

  const { visible, contentKey } = formPopups;
  const isNotificationsPopoverOpen = visible && contentKey === formPopupsContentKeys.NOTIFICATIONS ? true : false;

  const setFormPopupsAct = (visible, contentKey) => dispatch(ACTIONS.setFormPopups(visible, contentKey));

  // Helper function to track relevant patients click
  const trackRelevantPatientsClick = (notification) => {
    try {
      trackPendoEvent(dispatch, "Notification_Bell_View_Relevant_Patients", {
        programName: notification.assistanceProgram.name,
        numberOfRelevantPatients: notification.hasTopOppsPatients ? "Has patients" : "No patients", // We don't have exact count, so indicate presence
        notificationTimestamp: notification.createdAt,
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching notification Pendo event:", error);
    }
  };

  useEffect(() => {
    getNotifications();
  }, []);

  // Refresh notifications every time the bell popup opens
  useEffect(() => {
    if (isNotificationsPopoverOpen) {
      getNotifications();
    }
  }, [isNotificationsPopoverOpen]);

  useEffect(() => {
    if (!visible || contentKey !== formPopupsContentKeys.NOTIFICATIONS) return;
    const handleClickOutside = (event) => {
      const popoverElement = document.querySelector("#notifications_popover");

      if (popoverElement && !popoverElement.contains(event.target)) {
        setFormPopupsAct(false, formPopupsContentKeys.NOTIFICATIONS);
      }
    };
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [visible, contentKey]);

  useEffect(() => {
    const filteredData = DisplayFilterMap[displayFilter].filter(notifications);
    const sortedData = OrderSortMap[orderFilter].sort(filteredData);
    setFilteredNotifications(sortedData);
  }, [notifications, displayFilter, orderFilter]);

  const getNotifications = async () => {
    try {
      setIsFetching(true);
      const res = await getNotificationsAPI();
      if (res && res.data && res.data.notifications) {
        setNotifications(res.data.notifications);
      } else {
        ErrorMessage(t("notifications.get_notifications_failed"));
      }
    } catch (error) {
      ErrorMessage(t("notifications.get_notifications_failed"));
    } finally {
      setIsFetching(false);
    }
  };

  const handleMarkNotification = async (notificationID, val) => {
    try {
      setIsFetching(true);
      const resultRes = await markNotificationAPI(notificationID, val);
      if (resultRes && resultRes.data && resultRes.data.success) {
        getNotifications();
      } else {
        ErrorMessage(t("notifications.marking_notifications_failed"));
        setIsFetching(false);
      }
    } catch (error) {
      ErrorMessage(t("notifications.marking_notifications_failed"));
      setIsFetching(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      // Track Pendo event
      trackPendoEvent(dispatch, "Notification_Bell_MarkAllasRead", {
        username: user?.email,
        account
      });

      setIsFetching(true);

      const unreadNotifications = filteredNotifications.filter((notification) => !notification.read);

      if (unreadNotifications.length === 0) {
        setIsFetching(false);
        return;
      }

      const markPromises = unreadNotifications.map((notification) => markNotificationAPI(notification.id, true));

      const results = await Promise.all(markPromises);

      const allSuccessful = results.every((result) => result && result.data && result.data.success);

      if (allSuccessful) {
        getNotifications();
      } else {
        ErrorMessage(t("notifications.mark_all_as_read_failed"));
        setIsFetching(false);
      }
    } catch (error) {
      ErrorMessage(t("notifications.mark_all_as_read_failed"));
      setIsFetching(false);
    }
  };

  const handleMarkAllAsUnread = async () => {
    try {
      // Track Pendo event
      trackPendoEvent(dispatch, "Notification_Bell_MarkAllasUnread", {
        username: user?.email,
        account
      });

      setIsFetching(true);

      const readNotifications = filteredNotifications.filter((notification) => notification.read);

      if (readNotifications.length === 0) {
        setIsFetching(false);
        return;
      }

      const markPromises = readNotifications.map((notification) => markNotificationAPI(notification.id, false));

      const results = await Promise.all(markPromises);

      const allSuccessful = results.every((result) => result && result.data && result.data.success);

      if (allSuccessful) {
        getNotifications();
      } else {
        ErrorMessage(t("notifications.mark_all_as_unread_failed"));
        setIsFetching(false);
      }
    } catch (error) {
      ErrorMessage(t("notifications.mark_all_as_unread_failed"));
      setIsFetching(false);
    }
  };

  const getUnReadNotificationsAmount = () => filteredNotifications.filter((notification) => !notification.read).length;

  useEffect(() => {
    dispatch(ACTIONS.setBadgeCounts("notifications", getUnReadNotificationsAmount()));
  }, [filteredNotifications, dispatch]);

  const notificationValueMessage = (notification) => {
    const currentAPStatus = notification.assistanceProgram.status;

    const markAsReadButton = !notification.read && (
      <span
        style={{
          color: PRIMARY_BLUE,
          fontSize: "12px",
          fontWeight: 500,
          opacity: hoveredNotificationId === notification.id ? 1 : 0,
          pointerEvents: hoveredNotificationId === notification.id ? "auto" : "none",
          transition: "none",
          textDecoration: "underline",
          cursor: "pointer"
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleMarkNotification(notification.id, true);
        }}
        id="mark_as_read_btn"
      >
        {t("notifications.mark_as_read")}
      </span>
    );

    const markAsUnreadButton = notification.read && (
      <span
        style={{
          color: PRIMARY_BLUE,
          fontSize: "12px",
          fontWeight: 500,
          opacity: hoveredNotificationId === notification.id ? 1 : 0,
          pointerEvents: hoveredNotificationId === notification.id ? "auto" : "none",
          transition: "none",
          textDecoration: "underline",
          cursor: "pointer"
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleMarkNotification(notification.id, false);
        }}
        id="mark_as_unread_btn"
      >
        {t("notifications.mark_as_unread")}
      </span>
    );

    if (currentAPStatus == APStatus.CLOSED) {
      return (
        <Space size={16}>
          <Text style={{ color: SEC_FONT_GREY, fontSize: 12 }}>{t("notifications.enrollment_closed")}</Text>
          {markAsReadButton}
          {markAsUnreadButton}
        </Space>
      );
    } else if (notification.hasTopOppsPatients) {
      return (
        <Space size={16}>
          <Button
            type="primary"
            size="small"
            style={{
              backgroundColor: "#1570EF",
              borderColor: "#1570EF",
              fontSize: "14px",
              height: "30px",
              padding: "0 12px",
              width: "170px"
            }}
            onClick={(e) => {
              e.stopPropagation();
              trackRelevantPatientsClick(notification);
              setFormPopupsAct(false, formPopupsContentKeys.NOTIFICATIONS);
              history.push({
                pathname: Routes.ROOT + `top-opportunities/${notification.id}`,
                state: { notification, id: notification.id }
              });
            }}
            id="Notification_Bell_View_Relevant_Patients"
          >
            {t("notifications.view_relevant_patients")}
          </Button>
          {markAsReadButton}
          {markAsUnreadButton}
        </Space>
      );
    } else if (currentAPStatus == APStatus.WAITLIST_ONLY) {
      return (
        <Space size={16}>
          <Text style={{ color: SEC_FONT_GREY, fontSize: 12 }}>
            {t("notifications.no_relevant_patients_waitlist_only")}
          </Text>
          {markAsReadButton}
          {markAsUnreadButton}
        </Space>
      );
    } else {
      return (
        <Space size={16}>
          <Text style={{ color: SEC_FONT_GREY, fontSize: 12 }}>{t("notifications.no_relevant_patients")}</Text>
          {markAsReadButton}
          {markAsUnreadButton}
        </Space>
      );
    }
  };

  const handleFilterChange = (filterKey) => {
    setDisplayFilter(filterKey);
    dispatch(ACTIONS.setNotificationDisplayFilter(filterKey));
  };

  const handleOrderChange = (orderKey) => {
    setOrderFilter(orderKey);
  };

  const popoverTitle = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Text>{t("notifications.label")}</Text>
      <CloseOutlined
        id="close_notifications_popover"
        onClick={() => setFormPopupsAct(false, formPopupsContentKeys.NOTIFICATIONS)}
      />
    </div>
  );

  return (
    <>
      <NotificationDropdownStyles />
      <Popover
        id="notifications_popover"
        placement="bottomLeft"
        trigger="click"
        open={isNotificationsPopoverOpen}
        onOpenChange={(visible) => visible && setFormPopupsAct(visible, formPopupsContentKeys.NOTIFICATIONS)}
        title={popoverTitle}
        content={
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingLeft: 4,
                marginTop: 8,
                alignItems: "flex-start"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", flex: 1, paddingLeft: 15, height: 70 }}>
                <Space direction={"vertical"} size={0}>
                  <Text style={{ fontSize: 14, fontWeight: 600 }}>{t("notifications.title.display")}</Text>
                  <StyledSelect
                    onSelect={handleFilterChange}
                    dropdownMatchSelectWidth={false}
                    style={{ color: BLUE, fontSize: 12 }}
                    placeholder={<Text style={{ color: PRIMARY_BLUE }}> {DisplayFilterMap.unread.text(t)}</Text>}
                    bordered={false}
                    popupClassName="notification-dropdown"
                    id="notifications_display_filter"
                  >
                    {Object.values(DisplayFilterMap).map((displayFilter) => (
                      <Option
                        key={`display_${displayFilter.key}`}
                        id={`${displayFilter.key}_filter_option`}
                        value={displayFilter.key}
                      >
                        {displayFilter.text(t)}
                      </Option>
                    ))}
                  </StyledSelect>
                </Space>
                <Space direction={"vertical"} size={0}>
                  <Text style={{ fontSize: 14, fontWeight: 600 }}>{t("notifications.title.order_by")}</Text>
                  <StyledSelect
                    placeholder={<Text style={{ color: PRIMARY_BLUE }}> {OrderSortMap.newToOld.text(t)}</Text>}
                    onSelect={handleOrderChange}
                    dropdownMatchSelectWidth={false}
                    style={{ color: BLUE, fontSize: 12 }}
                    bordered={false}
                    popupClassName="notification-dropdown"
                    id="notifications_order_by_filter"
                  >
                    {Object.values(OrderSortMap).map((orderFilter) => (
                      <Option
                        key={`order_${orderFilter.key}`}
                        id={`${orderFilter.key}_filter_option`}
                        value={orderFilter.key}
                      >
                        {orderFilter.text(t)}
                      </Option>
                    ))}
                  </StyledSelect>
                </Space>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  width: "147px"
                }}
              >
                <Link
                  to="/notification-preferences"
                  style={{
                    textDecoration: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "42px",
                    marginTop: "13px",
                    marginRight: "8px"
                  }}
                  id="Notification_Bell_GoToSettings"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Track Pendo event
                    trackPendoEvent(dispatch, "Notification_Bell_GoToSettings", {
                      username: user?.email,
                      account
                    });
                    setFormPopupsAct(false, null);
                  }}
                >
                  <NotificationSettingsIcon style={{ cursor: "pointer" }} />
                  <Text style={{ fontSize: "10px", color: PRIMARY_BLUE }}>{t("notifications.settings")}</Text>
                </Link>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                  background: "#F9FAFB",
                  padding: "10px 15px",
                  height: "40px"
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {t("notifications.results_unread", {
                    results: filteredNotifications.length,
                    unread: getUnReadNotificationsAmount()
                  })}
                </div>
                {!isFetching && getUnReadNotificationsAmount() > 0 && filteredNotifications.length > 0 && (
                  <span
                    style={{
                      color: PRIMARY_BLUE,
                      fontSize: "12px",
                      fontWeight: 500,
                      textDecoration: "underline",
                      cursor: "pointer"
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAllAsRead();
                    }}
                    id="Notification_Bell_MarkAllasRead"
                  >
                    {t("notifications.mark_all_as_read")}
                  </span>
                )}
                {!isFetching && getUnReadNotificationsAmount() === 0 && filteredNotifications.length > 0 && (
                  <span
                    style={{
                      color: PRIMARY_BLUE,
                      fontSize: "12px",
                      fontWeight: 500,
                      textDecoration: "underline",
                      cursor: "pointer"
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAllAsUnread();
                    }}
                    id="Notification_Bell_MarkAllasUnread"
                  >
                    {t("notifications.mark_all_as_unread")}
                  </span>
                )}
              </div>
              <List
                style={{ overflowY: "auto" }}
                loading={isFetching}
                dataSource={filteredNotifications}
                rowKey={(notification) => notification.id}
                locale={{
                  emptyText: (
                    <div
                      style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}
                    >
                      <Space direction="vertical" align="center" size={8}>
                        <NoFundsFoundIcon style={{ color: GRAY_500 }} />
                        <Text
                          style={{
                            fontFamily: "Open Sans",
                            fontSize: "12px",
                            fontWeight: 400,
                            lineHeight: "18px",
                            color: GRAY_500
                          }}
                        >
                          {t("notifications.no_notifications")}
                        </Text>
                      </Space>
                    </div>
                  )
                }}
                renderItem={(notification) => (
                  <List.Item
                    id={`notification-${notification.id}`}
                    style={{
                      paddingLeft: "15px",

                      position: "relative",
                      background:
                        hoveredNotificationId === notification.id
                          ? "linear-gradient(90deg, #FFFFFF 0%, #EFF8FF 100%)"
                          : "transparent",
                      borderBottom: "1px solid #E4E7EC",
                      cursor: "pointer"
                    }}
                    onMouseEnter={() => setHoveredNotificationId(notification.id)}
                    onMouseLeave={() => setHoveredNotificationId(null)}
                    extra={
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                        >
                          {notification.assistanceProgram && (
                            <div style={{ padding: 16 }}>
                              {isUrl(notification.assistanceProgram.logo) ? (
                                <Image
                                  preview={false}
                                  src={notification.assistanceProgram.logo}
                                  style={{ width: 94, alignSelf: "center" }}
                                />
                              ) : (
                                <Skeleton.Image style={{ width: 94 }} />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    }
                  >
                    {!notification.read && (
                      <Badge
                        dot
                        size="default"
                        style={{
                          position: "absolute",
                          left: "0px", // Move closer to the left edge (or try -4px if needed)
                          top: "50%",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#1570EF",
                          borderRadius: "50%",
                          transform: "translateY(-50%)"
                        }}
                      />
                    )}
                    <List.Item.Meta
                      title={
                        <Text style={{ color: BLACK, fontSize: 12, fontWeight: 610, paddingLeft: "25px" }}>
                          {notification.assistanceProgram.status &&
                            t("notifications.program_value_and_moment", {
                              value: capitalizeFirstLetter(
                                t(`patientPotentialSavings.${notification.assistanceProgram.status.replace(" ", "_")}`)
                              ),
                              moment: dateTimeFormatter(notification.createdAt, "MM/DD/YYYY hh:mm A")
                            })}
                        </Text>
                      }
                      description={
                        <Space direction="vertical" style={{ width: "100%", paddingLeft: "25px" }}>
                          <Text style={{ maxLength: 10 }}>{notification.assistanceProgram.name}</Text>
                          <div>{notificationValueMessage(notification)}</div>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </>
        }
      >
        <Badge offset={[-7, 6]} size="small" count={badgeCounts?.notifications ?? 0}>
          <Tooltip title={t("notifications.tooltip")} placement="bottom">
            <CircleIconButton
              id="main_header_notifications"
              onClick={(e) => {
                e.stopPropagation();
                setFormPopupsAct(!isNotificationsPopoverOpen, formPopupsContentKeys.NOTIFICATIONS);
              }}
              icon={<BellFilled />}
            />
          </Tooltip>
        </Badge>
      </Popover>
    </>
  );
};

const Notifications = compose(withTranslation())(NotificationsComponent);
export { Notifications };
